import { useSessionContext } from '@/contexts/session-context';
import { signIn } from '@/lib/auth-client';
import { routes } from '@/lib/routes';
import { Loader2, Lock, Mail } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refetch } = useSessionContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn.email({
        email,
        password
      });

      // Refresh session context with new auth state
      await refetch();
      navigate(routes.inbox);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-medium text-ink text-center">{t('auth.welcomeBack')}</h2>

      {error && (
        <div className="bg-red/10 border border-red/20 text-red rounded-md p-3 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-sepia mb-1.5">
            {t('auth.email')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
              placeholder="you@example.com"
            />
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label htmlFor="password" className="block text-sm font-medium text-sepia">
              {t('auth.password')}
            </label>
            <Link to={routes.forgotPassword} className="text-sm text-blue hover:underline">
              {t('auth.forgotPassword')}
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
              placeholder={t('auth.password')}
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-ink text-cream rounded-md font-medium hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('common.loading')}
          </>
        ) : (
          t('auth.signIn')
        )}
      </button>

      <p className="text-center text-sm text-sepia">
        {t('auth.noAccount')}{' '}
        <Link to={routes.register} className="text-blue hover:underline font-medium">
          {t('auth.signUp')}
        </Link>
      </p>
    </form>
  );
}
